import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { parseISO } from 'date-fns';

import { useSchoolInfo } from './useSchoolInfo';
import { type ClassRecording } from '../components/classroom/types';
import { LEGACY_RECORDINGS_LIST, RECORDINGS_LIST } from '../config/apiPaths';
import { getUserUuidHeaders } from '../utils/httpUtils';
import { processApiPath } from '../utils/url';

type UseRecordingsT = {
    userUuid: string;
    showLegacyRecordings: boolean;
    doFetch: boolean;
    classUuid?: string;
    meetingId?: string;
};

export const useRecordings = (props: UseRecordingsT) => {
    const { userUuid, classUuid, meetingId, showLegacyRecordings, doFetch } = props;

    const { data: school } = useSchoolInfo();

    const schoolUuid = school?.uuid as string;

    const fetchLegacyRecordingData = useCallback(async (): Promise<ClassRecording[]> => {
        if (!meetingId) {
            return [];
        }

        try {
            const url = processApiPath(LEGACY_RECORDINGS_LIST, { meetingId });
            const { data } = await axios.get(url, { headers: getUserUuidHeaders(userUuid) });

            return data;
        } catch (e) {
            console.error(e);

            return [];
        }
    }, [meetingId, userUuid]);

    const fetchRecordingData = useCallback(async (): Promise<ClassRecording[]> => {
        if (!schoolUuid || !classUuid) {
            return [];
        }

        try {
            const url = processApiPath(RECORDINGS_LIST, { schoolUuid: schoolUuid, classUuid });
            const { data } = await axios.get(url, { headers: getUserUuidHeaders(userUuid) });

            // Sort recordings.
            const recs: ClassRecording[] = data.recordings.sort((a: ClassRecording, b: ClassRecording) => {
                if (parseISO(a.when) > parseISO(b.when)) {
                    return -1;
                }
                if (parseISO(a.when) < parseISO(b.when)) {
                    return 1;
                }
                return 0;
            });

            return recs;
        } catch (e) {
            console.error(e);
            return [];
        }
    }, [classUuid, schoolUuid, userUuid]);

    const queryKey = [RECORDINGS_LIST, classUuid];

    const {
        isLoading: isLoadingRecordings,
        isFetching: isFetchingRecordings,
        refetch: refetchRecordings,
        data
    } = useQuery({
        enabled: !!schoolUuid && !!classUuid && !!meetingId && doFetch,
        queryKey,
        refetchInterval: 15000,
        cacheTime: 15000,
        staleTime: 15000,
        refetchOnWindowFocus: true,
        refetchOnMount: false,
        queryFn: async () => await (showLegacyRecordings ? fetchLegacyRecordingData : fetchRecordingData)()
    });

    return {
        data,
        isLoadingRecordings,
        isFetchingRecordings,
        refetchRecordings
    };
};
